import { useMemo } from "react";
import useSWR from "swr";
import { vceFirestore, hvoFirestore } from "../../configs/FirebaseConfig";

const MARKETING_METRICS_COLLECTION = "marketingMetrics";

export const METRIC_SEGMENTS = {
  VCE_LANDELIJK: "vce_landelijk",
  VCE_REGIONAAL: "vce_regionaal",
  HVO_LANDELIJK: "hvo_landelijk",
};

/**
 * Fetcher function for marketing metrics from a specific Firestore instance
 */
const fetchMarketingMetrics = async (firestore, limit = 12) => {
  console.log("[useMarketingMetrics] 🔍 Fetching marketing metrics, limit:", limit);

  try {
    const snapshot = await firestore
      .collection(MARKETING_METRICS_COLLECTION)
      .orderBy("dateUnix", "desc")
      .limit(limit)
      .get();

    console.log("[useMarketingMetrics] ✅ Fetched", snapshot.size, "documents");

    if (snapshot.empty) {
      return [];
    }

    const results = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return results;
  } catch (error) {
    console.error("[useMarketingMetrics] ❌ Error fetching marketing metrics:", error);
    throw error;
  }
};

/**
 * Hook to fetch marketing metrics from Firestore
 * These metrics are pre-calculated by a Cloud Function and stored in the marketingMetrics collection.
 *
 * @param {Object} options - Configuration options
 * @param {string} options.segment - Which segment to display (default: vce_landelijk)
 * @param {number} options.historyLimit - Number of historical snapshots to fetch (default: 12)
 * @param {boolean} options.enabled - Whether to fetch data (default: true)
 * @returns {Object} - Marketing metrics data
 */
export const useMarketingMetrics = (options = {}) => {
  const { segment = METRIC_SEGMENTS.VCE_LANDELIJK, historyLimit = 12, enabled = true } = options;

  // Determine which Firestore instance to use based on segment
  const firestore = segment === METRIC_SEGMENTS.HVO_LANDELIJK ? hvoFirestore : vceFirestore;

  const {
    data: metricsHistory,
    error,
    isLoading,
    mutate,
  } = useSWR(
    enabled ? [`marketingMetrics`, segment, historyLimit] : null,
    ([_, _seg, limit]) => fetchMarketingMetrics(firestore, limit),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 300000, // 5 minutes - data changes infrequently
    },
  );

  /**
   * Extract the correct nested segment data from a document
   */
  const getSegmentData = (doc) => {
    if (!doc) return null;
    switch (segment) {
      case METRIC_SEGMENTS.VCE_LANDELIJK:
        return doc.landelijk || null;
      case METRIC_SEGMENTS.VCE_REGIONAAL:
        return doc.regionaal || null;
      case METRIC_SEGMENTS.HVO_LANDELIJK:
        // HVO has no landelijk/regionaal nesting — metrics are at top-level
        return doc;
      default:
        return doc.landelijk || null;
    }
  };

  /**
   * Flatten a raw document: extract the segment metrics to top-level
   */
  const flattenDoc = (doc) => {
    if (!doc) return null;
    const segmentData = getSegmentData(doc);
    if (!segmentData) return null;
    return {
      ...segmentData,
      id: doc.id,
      date: doc.date,
      dateUnix: doc.dateUnix,
      periodStart: doc.periodStart,
      periodEnd: doc.periodEnd,
      periodMonths: doc.periodMonths,
    };
  };

  /**
   * The most recent metrics snapshot
   */
  const latestMetrics = useMemo(() => {
    if (!metricsHistory || metricsHistory.length === 0) {
      return null;
    }
    return flattenDoc(metricsHistory[0]);
  }, [metricsHistory, segment]);

  /**
   * Previous metrics snapshot (for comparison)
   */
  const previousMetrics = useMemo(() => {
    if (!metricsHistory || metricsHistory.length < 2) {
      return null;
    }
    return flattenDoc(metricsHistory[1]);
  }, [metricsHistory, segment]);

  /**
   * Calculate percentage changes between latest and previous
   */
  const changes = useMemo(() => {
    if (!latestMetrics || !previousMetrics) {
      return {
        aov: null,
        purchaseFrequency: null,
        customerLifespanMonths: null,
        clv: null,
        repeatPurchaseRate: null,
        totalCustomers: null,
        totalOrders: null,
        totalRevenue: null,
      };
    }

    const calcChange = (current, previous) => {
      if (!previous || previous === 0) return null;
      return ((current - previous) / previous) * 100;
    };

    return {
      aov: calcChange(latestMetrics.aov, previousMetrics.aov),
      purchaseFrequency: calcChange(latestMetrics.purchaseFrequency, previousMetrics.purchaseFrequency),
      customerLifespanMonths: calcChange(latestMetrics.customerLifespanMonths, previousMetrics.customerLifespanMonths),
      clv: calcChange(latestMetrics.clv, previousMetrics.clv),
      repeatPurchaseRate: calcChange(latestMetrics.repeatPurchaseRate, previousMetrics.repeatPurchaseRate),
      totalCustomers: calcChange(latestMetrics.totalCustomers, previousMetrics.totalCustomers),
      totalOrders: calcChange(latestMetrics.totalOrders, previousMetrics.totalOrders),
      totalRevenue: calcChange(latestMetrics.totalRevenue, previousMetrics.totalRevenue),
    };
  }, [latestMetrics, previousMetrics]);

  /**
   * Get historical trend data for charts
   */
  const getTrendData = useMemo(() => {
    if (!metricsHistory || metricsHistory.length === 0) {
      return { labels: [], aov: [], frequency: [], lifespan: [], clv: [], repeatRate: [] };
    }

    // Reverse so oldest is first (chronological order)
    const sorted = [...metricsHistory].reverse();

    return {
      labels: sorted.map((m) => m.date),
      aov: sorted.map((m) => getSegmentData(m)?.aov || 0),
      frequency: sorted.map((m) => getSegmentData(m)?.purchaseFrequency || 0),
      lifespan: sorted.map((m) => getSegmentData(m)?.customerLifespanMonths || 0),
      clv: sorted.map((m) => getSegmentData(m)?.clv || 0),
      repeatRate: sorted.map((m) => getSegmentData(m)?.repeatPurchaseRate || 0),
    };
  }, [metricsHistory, segment]);

  return {
    latestMetrics,
    previousMetrics,
    metricsHistory: metricsHistory || [],
    changes,
    getTrendData,
    isLoading,
    error,
    refresh: mutate,
  };
};
